import Vue from 'vue'

import {
  getHighlight,
  getIndex,
  fetchCourse,
  fetchCourseModules,
  getCourseMaterials,
  getUserCoursesOngoing,
  getCourseAttendedPercent,
  generateCourseCertificate,
  createCourseClassActivityPercent,
  getUserCoursesWithCertificateAvailable,
} from '@/api/course.api'

import { getCategoryCourses } from '@/api/courseCategory.api'

const state = {
  courseHighlight: {},
  coursesHighlight: [],
  course: {},
  coursesByCategory: {},
  courseModules: [],
  courseModuleClass: null,
  coursesWithCertificateAvailable: [],
  courseMaterials: [],
  userCoursesOngoing: [],
  courseModuleTabActive: null,
}

const mutations = {
  resetCoursesByCategory (state, categoryId) {
    Vue.set(state.coursesByCategory, categoryId, [])
  },

  addCoursesByCategory (state, params) {
    const categoryId = params.category_id
    const courses = params.courses

    courses.forEach(course => {
      state.coursesByCategory[categoryId].courses.push(course)
    })
  },

  setCourseModuleTabActive (state, id) {
    state.courseModuleTabActive = id
  },

  setCourseMaterials (state, materials) {
    state.courseMaterials = materials
  },

  setCoursesWithCertificateAvailable (state, courses) {
    state.coursesWithCertificateAvailable = courses
  },

  setCourseHighlight (state, course) {
    state.courseHighlight = course
  },

  setCoursesHighlight (state, courses) {
    state.coursesHighlight = courses
  },

  setCourse (state, course) {
    state.course = course
  },

  setCourseModules (state, modules) {
    state.courseModules = modules
  },

  setCourseModuleClass (state, courseModuleClass) {
    state.courseModuleClass = courseModuleClass
  },

  setUserCoursesOngoing (state, courses) {
    state.userCoursesOngoing = courses
  },
}

const getters = {
  coursesByCategory ({ coursesByCategory }) {
    return (categoryId) => coursesByCategory[categoryId] 
  },

  courseModuleTabActive ({ courseModuleTabActive }) {
    return courseModuleTabActive
  },
  
  courseMaterials ({ courseMaterials }) {
    return courseMaterials
  },

  coursesWithCertificateAvailable ({ coursesWithCertificateAvailable }) {
    return coursesWithCertificateAvailable
  },

  courseModule(state, { courseModuleClass, courseModules, courseModuleClasses }) {
    if (!courseModuleClass) {
      return {}
    }

    return courseModules.find(courseModule => {
      const classes = courseModuleClasses(courseModule)
      return classes.findIndex(c => c.id == courseModuleClass.id) != -1
    })
  },

  courseHighlight (state) {
    return state.courseHighlight
  },

  coursesHighlight (state) {
    return state.coursesHighlight
  },

  course ({ course }) {
    return course
  },

  courseModules ({ courseModules }) {
    return courseModules
  },

  courseModuleClassVideoId (state, { courseModuleClass }) {
    return courseModuleClass.video_code
  },

  courseModuleClass ({ courseModuleClass, courseModules }) {
    if (!courseModuleClass && !courseModules.length) {
      return {}
    }

    if (!courseModuleClass) {
      return courseModules[0].course_classes_visible[0]
    }

    return courseModuleClass
  },

  courseModuleClasses () {
    return (courseModule) => courseModule.course_classes_visible
  },

  courseClasses (state, { courseModules, courseModuleClasses }) {
    return courseModules.reduce((carry, module) => {
      courseModuleClasses(module).forEach(module => carry.push(module))
      return carry
    }, [])
  },

  hasCourseAccess (state, { course }) {
    return course.has_user_access
  },

  isCourseCertificateAvailable (state, { course }) {
    return course.is_certificate_available
  },

  userCoursesOngoing ({ userCoursesOngoing }) {
    return userCoursesOngoing
  },

  courseUrl () {
    return (course) => ({
      name: 'course.show',
      params: {
        id: course.id,
        slug: course.slug
      }
    })
  },
}

const actions = {
  async fetchCoursesByCategory ({ state, commit }, categoryId) {
    if (typeof state.coursesByCategory[categoryId] === 'undefined') {
      Vue.set(state.coursesByCategory, categoryId, { page: 1, courses: [] })
    }

    const page = state.coursesByCategory[categoryId].page
    if (page === null) return null

    const res = await getCategoryCourses(categoryId, { page })
    const courses = res.data

    console.log('links', res.links)

    if (!res.links.next) {
      state.coursesByCategory[categoryId].page = null
    } else {
      state.coursesByCategory[categoryId].page++
    }


    commit('addCoursesByCategory', {
      category_id: categoryId,
      courses
    })

    return true
  },

  async fetchCourseMaterials ({ commit, getters }) {
    const course = getters.course
    const materials = await getCourseMaterials(course.id)
    commit('setCourseMaterials', materials)
  },

  async fetchUserCoursesWithCertificateAvailable ({ commit }) {
    const courses = await getUserCoursesWithCertificateAvailable()
    commit('setCoursesWithCertificateAvailable', courses)
  },

  async updateCourseAttendedPercent ({ getters }) {
    const course = getters.course

    const attendedPercent = await getCourseAttendedPercent(course.id)
    if (attendedPercent >= 80) {
      course.is_certificate_available = true
    }

    course.attended_percent = attendedPercent
  },

  async generateCourseCertificate ({ getters }, course) {
    course = course || getters.course
    const certificateUrl = await generateCourseCertificate(course.id)

    return certificateUrl
  },

  async createCourseClassActivityPercent ({ getters, dispatch }, percent = 0) {
    if (percent >= 80) {
      getters.courseModuleClass.attended_percent = percent
    }

    await createCourseClassActivityPercent(getters.courseModuleClass.id, percent)
    
    await dispatch('updateCourseAttendedPercent')
  },

  async fetchCourse ({ commit }, courseId) {
    const course = await fetchCourse (courseId)
    commit('setCourse', course)
  },

  async fetchHighlight ({ commit }) {
    const course = await getHighlight()
    commit('setCourseHighlight', course)
  },

  async fetchCoursesHighlight ({ commit }) {
    const courses = await getIndex({ perpage: 12 })

    commit('setCoursesHighlight', courses)
  },

  async fetchCourseModules ({ commit }, courseId) {
    const modules = await fetchCourseModules(courseId)

    commit('setCourseModules', modules)
  },

  async fetchUserCoursesOngoing ({ commit }) {
    const res = await getUserCoursesOngoing('me')

    commit('setUserCoursesOngoing', res.data)
  }
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}
